import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { FileEditor } from './FileEditor';

interface Props {
  filePath: string;
  initialContent: string;
  prTitle: string;
  isEdit?: boolean;
  token: string;
  onFirstSave: (result: { prNumber: number; commitSha: string }) => void;
  onCancel: () => void;
  onAuthError: () => void;
  registerNavigationGuard?: (guard: (action: () => void) => void) => void;
}

export function DirectEditPane({
  filePath,
  initialContent,
  prTitle,
  isEdit,
  token,
  onFirstSave,
  onCancel,
  onAuthError,
  registerNavigationGuard,
}: Props) {
  const [dirty, setDirty] = useState(false);
  const [pendingNav, setPendingNav] = useState<{ action: () => void } | null>(null);

  const guardNavigation = useCallback((action: () => void) => {
    if (dirty) {
      setPendingNav({ action });
    } else {
      action();
    }
  }, [dirty]);

  useEffect(() => {
    registerNavigationGuard?.(guardNavigation);
    return () => registerNavigationGuard?.((a) => a());
  }, [guardNavigation, registerNavigationGuard]);

  return (
    <main className="flex-1 flex flex-col h-screen min-w-0">
      <header className="px-4 py-3 border-b flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-semibold truncate">{prTitle}</h2>
          <p className="text-xs text-muted-foreground font-mono truncate">{filePath}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => guardNavigation(onCancel)}>
          Cancel
        </Button>
      </header>

      <div className="flex-1 flex flex-col min-h-0">
        <FileEditor
          mode={{ kind: 'new', initialContent }}
          token={token}
          filePath={filePath}
          prTitle={prTitle}
          onFirstSave={onFirstSave}
          onSaveCommit={() => {}}
          onAuthError={onAuthError}
          onDirtyChange={setDirty}
          actionLabel={isEdit ? { save: 'Update article', saving: 'Updating…' } : undefined}
        />
      </div>

      <Dialog open={!!pendingNav} onOpenChange={(o) => !o && setPendingNav(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unsaved changes</DialogTitle>
            <DialogDescription>
              Your edits haven't been saved and will be lost if you continue.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingNav(null)}>
              Stay here
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                const action = pendingNav!.action;
                setPendingNav(null);
                action();
              }}
            >
              Discard changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
